"use client";

import React, { useState, useEffect } from "react";
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap";
import { toast } from "react-toastify";
import Api from "../../api/Api";

//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";
import ServiceTableItem from "./ServiceTableItem";
import ServiceDetailsModal from "./ServiceDetailsModal";

const Services = () => {
  const [modal_center, setmodal_center] = useState(false);
  const [selectedService, setSelectedService] = useState({});
  const [services, setServices] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [categories, setCategories] = useState([]);

  // Pagination states
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  document.title = "Services | Tafaseel Admin";

  const statusOptions = [
    { label: "All Statuses", value: "" },
    { label: "Active", value: "1" },
    { label: "Inactive", value: "0" },
  ];

  useEffect(() => {
    fetchServices();
    fetchCategories();
  }, [
    currentPage,
    searchQuery,
    statusFilter,
    categoryFilter,
    dateFrom,
    dateTo,
    minPrice,
    maxPrice,
  ]);

  // Fetch categories for filter
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await Api.get("/admin/service-categories", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Fetch services from API
  const fetchServices = async () => {
    try {
      setLoading(true);

      const params = {
        page: currentPage,
        per_page: itemsPerPage,
        search: searchQuery,
        is_active: statusFilter,
        category_id: categoryFilter,
        date_from: dateFrom,
        date_to: dateTo,
        min_price: minPrice,
        max_price: maxPrice,
      };

      // Remove empty parameters
      Object.keys(params).forEach((key) => {
        if (
          params[key] === "" ||
          params[key] === null ||
          params[key] === undefined
        ) {
          delete params[key];
        }
      });

      const token = localStorage.getItem("token");
      const response = await Api.get("/admin/services", {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setServices(response.data.data);
        setFilteredData([]);
      } else {
        toast.error(response.data.message || "Failed to fetch services", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error fetching services:", error);
      toast.error("Network error. Please check your connection.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setLoading(false);
    }
  };

  function tog_center() {
    setmodal_center(!modal_center);
    setSelectedService({});
  }

  const handleShow = async (service) => {
    try {
      const token = localStorage.getItem("token");
      const response = await Api.get(`/admin/services/${service.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setSelectedService(response.data.data);
        setmodal_center(true);
      } else {
        toast.error(
          response.data.message || "Failed to fetch service details",
          {
            position: "bottom-center",
            autoClose: 3000,
            theme: "colored",
          }
        );
      }
    } catch (error) {
      console.error("Error fetching service details:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    }
  };

  const handleDelete = async (serviceId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this service? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await Api.delete(`/admin/services/${serviceId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        toast.success("Service deleted successfully", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });

        // Remove service from local state
        setServices(services.filter((service) => service.id !== serviceId));
        setFilteredData(
          filteredData.filter((service) => service.id !== serviceId)
        );
      } else {
        toast.error(response.data.message || "Failed to delete service", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error deleting service:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    }
  };

  const handleUpdateServiceStatus = async (serviceId, newStatus) => {
    try {
      const token = localStorage.getItem("token");
      const response = await Api.put(
        `/admin/services/${serviceId}/status`,
        {
          is_active: newStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Service status updated successfully", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });

        // Update service status in local state
        setServices(
          services.map((service) =>
            service.id === serviceId
              ? { ...service, is_active: newStatus }
              : service
          )
        );

        if (filteredData.length > 0) {
          setFilteredData(
            filteredData.map((service) =>
              service.id === serviceId
                ? { ...service, is_active: newStatus }
                : service
            )
          );
        }

        if (selectedService && selectedService.id === serviceId) {
          setSelectedService({
            ...selectedService,
            is_active: newStatus,
          });
        }
      } else {
        toast.error(
          response.data.message || "Failed to update service status",
          {
            position: "bottom-center",
            autoClose: 3000,
            theme: "colored",
          }
        );
      }
    } catch (error) {
      console.error("Error updating service status:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    if (query === "" && statusFilter === "" && categoryFilter === "") {
      setFilteredData([]);
      setCurrentPage(1);
      return;
    }

    let filtered = services;

    // Apply search filter
    if (query !== "") {
      filtered = filtered.filter(
        (service) =>
          service.title?.toLowerCase().includes(query) ||
          service.description?.toLowerCase().includes(query) ||
          service.user?.name?.toLowerCase().includes(query) ||
          service.user?.email?.toLowerCase().includes(query) ||
          service.category?.name?.toLowerCase().includes(query) ||
          service.id.toString().includes(query)
      );
    }

    // Apply status filter
    if (statusFilter !== "") {
      filtered = filtered.filter(
        (service) => service.is_active.toString() === statusFilter
      );
    }

    // Apply category filter
    if (categoryFilter !== "") {
      filtered = filtered.filter(
        (service) => service.category_id.toString() === categoryFilter
      );
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    applyFilters();
  };

  const handleCategoryFilter = (categoryId) => {
    setCategoryFilter(categoryId);
    applyFilters();
  };

  const applyFilters = () => {
    setCurrentPage(1);
    setShowFiltersModal(false);
    fetchServices();
  };

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setMinPrice("");
    setMaxPrice("");
    setStatusFilter("");
    setCategoryFilter("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  // Get service counts by status
  const getStatusCounts = () => {
    const counts = {
      all: services.length,
      active: services.filter((s) => s.is_active === 1).length,
      inactive: services.filter((s) => s.is_active === 0).length,
    };
    return counts;
  };

  const statusCounts = getStatusCounts();

  // Pagination logic
  const dataToShow = filteredData.length > 0 ? filteredData : services;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = dataToShow.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(dataToShow.length / itemsPerPage);

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const setPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbers = 10;
    const maxVisiblePages = Math.min(totalPages, maxPageNumbers);

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Tafaseel Admin" breadcrumbItem="Services" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div id="serviceList">
                    <Row className="g-4 mb-3">
                      <Col className="col-sm-auto">
                        <div className="d-flex gap-1">
                          <Button
                            color="primary"
                            className="refresh-btn"
                            onClick={fetchServices}
                            disabled={loading}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>
                            {loading ? "Loading..." : "Refresh"}
                          </Button>
                          <Button
                            color="info"
                            className="filters-btn"
                            onClick={() => setShowFiltersModal(true)}
                          >
                            <i className="ri-filter-line align-bottom me-1"></i>
                            Advanced Filters
                          </Button>
                        </div>
                      </Col>
                      <Col className="col-sm">
                        <div className="d-flex justify-content-sm-end gap-2">
                          <div className="category-filter">
                            <select
                              className="form-select"
                              value={categoryFilter}
                              onChange={(e) =>
                                handleCategoryFilter(e.target.value)
                              }
                            >
                              <option value="">All Categories</option>
                              {categories.map((category) => (
                                <option key={category.id} value={category.id}>
                                  {category.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="status-filter">
                            <select
                              className="form-select"
                              value={statusFilter}
                              onChange={(e) =>
                                handleStatusFilter(e.target.value)
                              }
                            >
                              {statusOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="search-box">
                            <input
                              type="text"
                              className="form-control search"
                              placeholder="Search services..."
                              value={searchQuery}
                              onChange={handleSearch}
                            />
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {/* Status Summary Cards */}
                    <Row className="mb-3">
                      <Col>
                        <div className="d-flex gap-3 flex-wrap">
                          <div className="status-card p-2 border rounded">
                            <small className="text-muted">Total</small>
                            <div className="fw-bold">{statusCounts.all}</div>
                          </div>
                          <div className="status-card p-2 border rounded border-success">
                            <small className="text-success">Active</small>
                            <div className="fw-bold text-success">
                              {statusCounts.active}
                            </div>
                          </div>
                          <div className="status-card p-2 border rounded border-danger">
                            <small className="text-danger">Inactive</small>
                            <div className="fw-bold text-danger">
                              {statusCounts.inactive}
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="table-responsive table-card mt-3 mb-1">
                      {loading ? (
                        <div className="text-center p-4">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="sr-only">Loading...</span>
                          </div>
                          <p className="mt-2">Loading services...</p>
                        </div>
                      ) : (
                        <table
                          className="table align-middle table-nowrap"
                          id="serviceTable"
                        >
                          <thead className="table-light">
                            <tr>
                              <th className="sort" data-sort="service_id">
                                Service ID
                              </th>
                              <th className="sort" data-sort="service">
                                Service
                              </th>
                              <th className="sort" data-sort="provider">
                                Provider
                              </th>
                              <th className="sort" data-sort="category">
                                Category
                              </th>
                              <th className="sort" data-sort="price">
                                Price
                              </th>
                              <th className="sort" data-sort="status">
                                Status
                              </th>
                              <th className="sort" data-sort="created_at">
                                Created
                              </th>
                              <th className="sort" data-sort="action">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="list form-check-all">
                            {currentItems.length > 0 ? (
                              currentItems.map((service) => (
                                <ServiceTableItem
                                  key={service.id}
                                  service={service}
                                  handleShow={handleShow}
                                  handleDelete={handleDelete}
                                  handleUpdateStatus={handleUpdateServiceStatus}
                                />
                              ))
                            ) : (
                              <tr>
                                <td colSpan="8" className="text-center p-4">
                                  <div className="text-center">
                                    <lord-icon
                                      src="https://cdn.lordicon.com/msoeawqm.json"
                                      trigger="loop"
                                      colors="primary:#121331,secondary:#08a88a"
                                      style={{ width: "75px", height: "75px" }}
                                    ></lord-icon>
                                    <h5 className="mt-2">No Services Found</h5>
                                    <p className="text-muted mb-0">
                                      {searchQuery ||
                                      statusFilter ||
                                      categoryFilter
                                        ? `No services found matching your criteria`
                                        : "No services available at the moment."}
                                    </p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      )}

                      {!loading &&
                        dataToShow.length === 0 &&
                        !searchQuery &&
                        !statusFilter &&
                        !categoryFilter && (
                          <div className="noresult text-center p-4">
                            <lord-icon
                              src="https://cdn.lordicon.com/msoeawqm.json"
                              trigger="loop"
                              colors="primary:#121331,secondary:#08a88a"
                              style={{ width: "75px", height: "75px" }}
                            ></lord-icon>
                            <h5 className="mt-2">No Services Available</h5>
                            <p className="text-muted mb-0">
                              There are no services in the system yet.
                            </p>
                          </div>
                        )}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-center">
                        <div className="pagination-wrap hstack gap-2">
                          <div className="d-flex justify-content-end">
                            <div
                              className="btn-group me-2"
                              role="group"
                              aria-label="Pagination"
                            >
                              <button
                                className="page-item pagination-prev btn btn-primary"
                                onClick={prevPage}
                                type="button"
                                disabled={currentPage === 1}
                              >
                                Previous
                              </button>

                              {getPageNumbers().map((pageNumber) => (
                                <button
                                  type="button"
                                  className={`btn ${
                                    pageNumber === currentPage
                                      ? "btn-primary"
                                      : "btn-outline-primary"
                                  }`}
                                  key={pageNumber}
                                  onClick={() => setPage(pageNumber)}
                                >
                                  {pageNumber}
                                </button>
                              ))}

                              <button
                                disabled={currentPage === totalPages}
                                className="page-item pagination-next btn btn-primary"
                                type="button"
                                onClick={nextPage}
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Show results info */}
                    {dataToShow.length > 0 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="text-muted">
                          Showing {indexOfFirstItem + 1} to{" "}
                          {Math.min(indexOfLastItem, dataToShow.length)} of{" "}
                          {dataToShow.length} services
                        </div>
                        <div className="text-muted">
                          Page {currentPage} of {totalPages}
                        </div>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Service Details Modal */}
          {modal_center && (
            <ServiceDetailsModal
              modal_center={modal_center}
              setmodal_center={setmodal_center}
              tog_center={tog_center}
              service={selectedService}
              onStatusUpdate={handleUpdateServiceStatus}
            />
          )}

          {/* Advanced Filters Modal */}
          {showFiltersModal && (
            <div
              className="modal fade show d-block"
              tabIndex="-1"
              role="dialog"
            >
              <div
                className="modal-dialog modal-dialog-centered"
                role="document"
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Advanced Filters</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowFiltersModal(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">Date From</label>
                          <input
                            type="date"
                            className="form-control"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                          />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">Date To</label>
                          <input
                            type="date"
                            className="form-control"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                          />
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">Min Price</label>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Min Price"
                            value={minPrice}
                            onChange={(e) => setMinPrice(e.target.value)}
                          />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">Max Price</label>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Max Price"
                            value={maxPrice}
                            onChange={(e) => setMaxPrice(e.target.value)}
                          />
                        </div>
                      </Col>
                    </Row>
                  </div>
                  <div className="modal-footer">
                    <Button color="secondary" onClick={clearFilters}>
                      Clear All
                    </Button>
                    <Button color="primary" onClick={applyFilters}>
                      Apply Filters
                    </Button>
                  </div>
                </div>
              </div>
              <div className="modal-backdrop fade show"></div>
            </div>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Services;
